import { useEffect, useState } from 'react';
import { get } from '@/api/client';
import { getApiBaseURL } from '@/lib/platform';

/**
 * 把后端下发的相对媒体路径（/uploads/...）解析为当前运行环境可达的 URL：
 * - 绝对 URL 原样返回（兼容历史落库数据）
 * - 相对路径拼 API origin：Web(/api 同源) 下为空串走 vite 代理；
 *   Native 壳内 API 是绝对地址（http://host:port/api），拼出后端 origin
 */
function resolveMediaUrl(url: string): string {
  if (!url || url.startsWith('http://') || url.startsWith('https://')) return url;
  const origin = getApiBaseURL().replace(/\/api\/?$/, '');
  return url.startsWith('/') ? `${origin}${url}` : `${origin}/${url}`;
}

/** 单个平台的下载读数（后台「站点信息 → App 下载页」维护，经 /public/config 下发） */
export interface AppDownloadPlatform {
  /** 是否已上架可下载；false 时下载页该平台卡整块置灰走「敬请期待」 */
  enabled: boolean;
  /** 下载/商店链接：Android 为自托管 APK 直链，iOS/鸿蒙为商店链接；空串不渲染按钮 */
  url: string;
  version: string;
  /** 以下仅 Android 提供：包体大小、更新时间（展示用自由文本）、更新说明 */
  size?: string;
  updatedAt?: string;
  changelog?: string;
}

export interface SiteConfig {
  siteName: string;
  logoUrl: string;
  faviconUrl: string;
  /** 页脚版权行，主体须为备案主体全称；空则页脚不显示版权行 */
  copyright: string;
  /** 本主体自己的 ICP 备案号；空则不显示（挂他主体的号属于冒用） */
  icpNumber: string;
  /** 公安备案号，未下发则空、不显示 */
  policeNumber: string;
  contactPhone: string;
  contactEmail: string;
  /** 对外服务时间（展示用自由文本，如「09:00-18:00」）；空则联系页整行不显示 */
  contactHours: string;
  address: string;
  /** 注册条款强制阅读倒计时（秒），0 表示无需等待；来自后台「terms.countdownSeconds」 */
  termsCountdownSeconds: number;
  /** App 下载页三平台读数；后台未配置时全部 enabled=false（下载页走「敬请期待」） */
  appDownloads: {
    android: AppDownloadPlatform;
    ios: AppDownloadPlatform;
    harmony: AppDownloadPlatform;
  };
  /**
   * 落地页/关于页四格统计读数（后台「站点信息」按真实统计填写的自由文本）。
   * 空串＝未配置，对应那一格整块不渲染；四格全空则整块不出现。
   * 这里刻意不给任何兜底数字：这四格曾是写死的口号式数量级，属广告引证内容，
   * 拿不到真值时只能不显示，兜底一个编出来的数就是本次改造要消除的东西。
   */
  landingStats: {
    users: string;
    orders: string;
    years: string;
    response: string;
  };
}

/**
 * 落地页（/）、登录页（/login）、关于页（/about）共用的四格统计读数。
 * 值来自后台「站点信息」，只保留配了真读数的格；四格全空返回空数组，页面据此整块不渲染。
 * 副标签留在代码里而不是再加四列配置：同一份读数在三页必须显示成同一句话，
 * 交给后台自由填写反而会让三处口径分叉。
 */
export function landingStatCells(
  stats: SiteConfig['landingStats'],
): Array<{ value: string; label: string }> {
  return [
    { value: stats.users, label: '活跃用户' },
    { value: stats.orders, label: '累计订单' },
    { value: stats.years, label: '稳定运行' },
    { value: stats.response, label: '服务响应' },
  ].filter((cell) => cell.value !== '');
}

// 硬编码兜底：仅站点名给值（＝备案网站名称，后台没配也不至于标题空白），
// logo 兜底保持现有视觉，favicon 回退本地静态资源；
// 倒计时兜底 30 秒与注册页历史行为一致（仅在 /public/config 不可达时生效）。
// 备案号 / 版权主体 / 联系方式一律留空：这些是法定对外事实，
// 拿不到真值时只能不显示，兜底一个编出来的号就是冒用或联系方式不实。
const FALLBACK_SITE_CONFIG: SiteConfig = {
  siteName: '活动精英订单预约管理系统',
  // 兜底图会在后台未配置时被每个访客下载一次，所以必须是站内小文件：
  // 原来是远端 1.9 MB 的 2160px PNG，而实际最大显示尺寸只有 40px。
  logoUrl: '/brand/logo.png',
  faviconUrl: '/favicon.ico',
  copyright: '',
  icpNumber: '',
  policeNumber: '',
  contactPhone: '',
  contactEmail: '',
  contactHours: '',
  address: '',
  termsCountdownSeconds: 60,
  // 拿不到后台配置时三端一律「未上架」：下载页据此整块置灰走「敬请期待」，
  // 不凭空造一个下载链接（编出来的 APK 地址点了就是死链，比不显示更糟）。
  appDownloads: {
    android: { enabled: false, url: '', version: '' },
    ios: { enabled: false, url: '', version: '' },
    harmony: { enabled: false, url: '', version: '' },
  },
  // 后台不可达时四格一律留空：落地页据此整块不渲染统计区，
  // 而不是在拿不到真读数的情况下把某个数字当成事实显示出去。
  landingStats: { users: '', orders: '', years: '', response: '' },
};

// countdownSeconds 规范化：非负整数有效（0 = 无需等待），其余回退兜底值
function sanitizeCountdown(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : fallback;
}

// 模块级缓存：整个应用生命周期只请求一次 /public/config
let cachedConfig: SiteConfig | null = null;
let configPromise: Promise<SiteConfig> | null = null;

async function loadSiteConfig(): Promise<SiteConfig> {
  if (cachedConfig) return cachedConfig;
  if (!configPromise) {
    configPromise = get<{
      siteName?: string;
      logoUrl?: string;
      faviconUrl?: string;
      copyright?: string;
      icpNumber?: string;
      policeNumber?: string;
      contactPhone?: string;
      contactEmail?: string;
      contactHours?: string;
      address?: string;
      terms?: { countdownSeconds?: number };
      appDownloads?: {
        android?: Partial<AppDownloadPlatform>;
        ios?: Partial<AppDownloadPlatform>;
        harmony?: Partial<AppDownloadPlatform>;
      };
      landingStats?: {
        users?: string;
        orders?: string;
        years?: string;
        response?: string;
      };
    }>('/public/config')
      .then((data) => {
        const text = (value: string | undefined, fallback: string) =>
          value?.trim() || fallback;
        // 平台下载读数归一：enabled 只认真布尔（缺省＝未上架），其余是去边缘空白的文本。
        // 逐字段兜底而非整块替换——后台只配了 Android、iOS 段缺失时不能让整份配置作废。
        const platform = (
          raw: Partial<AppDownloadPlatform> | undefined,
        ): AppDownloadPlatform => ({
          enabled: raw?.enabled === true,
          url: (raw?.url ?? '').trim(),
          version: (raw?.version ?? '').trim(),
          size: (raw?.size ?? '').trim(),
          updatedAt: (raw?.updatedAt ?? '').trim(),
          changelog: (raw?.changelog ?? '').trim(),
        });
        cachedConfig = {
          siteName: text(data?.siteName, FALLBACK_SITE_CONFIG.siteName),
          logoUrl: data?.logoUrl?.trim()
            ? resolveMediaUrl(data.logoUrl.trim())
            : FALLBACK_SITE_CONFIG.logoUrl,
          faviconUrl: data?.faviconUrl?.trim()
            ? resolveMediaUrl(data.faviconUrl.trim())
            : FALLBACK_SITE_CONFIG.faviconUrl,
          copyright: text(data?.copyright, FALLBACK_SITE_CONFIG.copyright),
          icpNumber: text(data?.icpNumber, FALLBACK_SITE_CONFIG.icpNumber),
          policeNumber: text(data?.policeNumber, FALLBACK_SITE_CONFIG.policeNumber),
          contactPhone: text(data?.contactPhone, FALLBACK_SITE_CONFIG.contactPhone),
          contactEmail: text(data?.contactEmail, FALLBACK_SITE_CONFIG.contactEmail),
          contactHours: text(data?.contactHours, FALLBACK_SITE_CONFIG.contactHours),
          address: text(data?.address, FALLBACK_SITE_CONFIG.address),
          termsCountdownSeconds: sanitizeCountdown(
            data?.terms?.countdownSeconds,
            FALLBACK_SITE_CONFIG.termsCountdownSeconds,
          ),
          appDownloads: {
            android: platform(data?.appDownloads?.android),
            ios: platform(data?.appDownloads?.ios),
            harmony: platform(data?.appDownloads?.harmony),
          },
          // 四格读数逐字段取真值、缺失即空串：这里绝不给兜底数字（见 FALLBACK 处注释）。
          landingStats: {
            users: (data?.landingStats?.users ?? '').trim(),
            orders: (data?.landingStats?.orders ?? '').trim(),
            years: (data?.landingStats?.years ?? '').trim(),
            response: (data?.landingStats?.response ?? '').trim(),
          },
        };
        return cachedConfig;
      })
      .catch(() => {
        configPromise = null; // 失败后允许下次重试
        return FALLBACK_SITE_CONFIG;
      });
  }
  return configPromise;
}

/**
 * 站点配置（名称 / logo / favicon）：
 * 由后台「系统设置 → 站点信息」统一配置，经 GET /public/config 公开下发。
 */
export function useSiteConfig(): SiteConfig {
  const [config, setConfig] = useState<SiteConfig>(cachedConfig ?? FALLBACK_SITE_CONFIG);

  useEffect(() => {
    let cancelled = false;
    loadSiteConfig().then((value) => {
      if (!cancelled) setConfig(value);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return config;
}